import {
  EntityType,
  trackerApi,
  WatcherEntitySummary,
} from '@texas/api/endpoints/trackerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { SpinnerWithLabel } from '../SpinnerWithLabel';
import { Flex, Button, Text, useToast } from '@chakra-ui/react';
import { trackerConfig, TrackerConfigProps } from './config';
import { NoData } from '../NoData';
import { entityTypeFriendlyName } from './translations';
import { trackerEvents } from './events';
import { request } from '@texas/utils/helpers/httpHelpers';
import { MetadataList, MetadataListRow } from '../MetadataList';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '../alert/ErrorDetails';
import { convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { useTranslation } from 'react-i18next';

export function TrackerConfig({
  entityType,
  entityId,
}: {
  entityType: EntityType;
  entityId: number;
}) {
  const toast = useToast();
  const { t } = useTranslation();

  const { refetch, data, loading } = useApiResource(trackerApi.get);
  const {
    request: watchRequest,
    loading: watchLoading,
    error: watchError,
  } = useApiRequest(trackerApi.addWatcher);
  const {
    request: dewatchRequest,
    loading: dewatchLoading,
    error: dewatchError,
  } = useApiRequest(trackerApi.removeWatcher);

  const performWatch = (toTrack: EntityType) =>
    request(
      watchRequest,
      [{ entityId, entityType, watchEntityType: toTrack }],
      () => {
        trackerEvents.onAdded.dispatch();
        refetch(entityId, entityType);
      },
    );

  const performUnwatch = (watcherId: number) =>
    request(dewatchRequest, [watcherId], (response) => {
      trackerEvents.onAdded.dispatch();
      refetch(entityId, entityType);

      if (response.failedUnsubscribedIds.length > 0) {
        toast({
          status: 'warning',
          title: t('tracking.untrackErrorTitle'),
          description: t('tracking.untrackErrorDesc', {
            count: response.failedUnsubscribedIds.length,
            type: entityTypeFriendlyName(t, response.entityType),
          }),
        });
      }
    });

  useEffect(() => {
    refetch(entityId, entityType);
  }, [entityId, entityType, refetch]);

  if (loading) {
    return <SpinnerWithLabel label={t('general.loading')} />;
  }

  if (!data) {
    return <NoData label={t('general.noData')} />;
  }

  const config = trackerConfig[entityType];

  if (!config) {
    return <>{t('tracking.notFound')}</>;
  }

  return (
    <>
      {watchError && <ErrorDetails error={watchError} />}
      {dewatchError && <ErrorDetails error={dewatchError} />}
      <TrackingConfigComponent
        watcherSum={data}
        config={config}
        isLoading={watchLoading || dewatchLoading}
        onWatch={(entity) => performWatch(entity)}
        onStopWatch={(id) => performUnwatch(id)}
      />
    </>
  );
}

function TrackingConfigComponent({
  watcherSum,
  config,
  isLoading,
  onWatch,
  onStopWatch,
}: {
  watcherSum: WatcherEntitySummary;
  config: TrackerConfigProps;
  isLoading: boolean;
  onWatch: (type: EntityType) => void;
  onStopWatch: (watchId: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <Flex flexDir="column" gap={2}>
      <MetadataList>
        {config.canTrack.map((x) => {
          const watcher = watcherSum.data[convertToEnum(EntityType, x)];
          return (
            <MetadataListRow
              alignItems="center"
              key={x}
              label={t('tracking.trackType', {
                type: entityTypeFriendlyName(t, x),
              })}
            >
              {watcher ? (
                <Flex gap={2} alignItems="center">
                  <Text>
                    {watcher.nrOfSubscribedTrackers}/{watcher.totalTrackers}
                  </Text>
                  {watcher.watcherId ? (
                    <Button
                      size="sm"
                      onClick={() => onStopWatch(watcher.watcherId!)}
                      isLoading={isLoading}
                      isDisabled={isLoading}
                    >
                      {t('tracking.untrack')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => onWatch(x)}
                      isLoading={isLoading}
                      isDisabled={isLoading}
                    >
                      {t('tracking.track')}
                    </Button>
                  )}
                </Flex>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={() => onWatch(x)}
                    isLoading={isLoading}
                    isDisabled={isLoading}
                  >
                    {t('tracking.track')}
                  </Button>
                </>
              )}
            </MetadataListRow>
          );
        })}
      </MetadataList>
    </Flex>
  );
}
